import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FolderOpen,
  Search,
  ChevronRight,
  Layers,
  TrendingUp,
  Clock,
} from 'lucide-react';

// ── Data ──
import { allCategories } from '../../helpers/Project-Report/data/allCategories';
import { fetchProjectCategoriesSummary } from '../../services/projectReportServices';
import { mergeCategoriesData } from '../../helpers/Project-Report/utils/mergeCategoriesData';

// ── Components ──
import SummaryCard from './components/SummaryCard';
import CategoryCard from './components/CategoryCard';

import { useCurrentUserAccess } from '../../helpers/currentUserAccess';

// ─── Main Page ─────────────────────────────────────────────────────────────────
const ProjectCategories = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('Semua');
  const [isLoading, setIsLoading] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const nik = searchParams.get('nik') || null;
  const { isCheckingAuth } = useCurrentUserAccess(nik);

  // Fetch API
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const result = await fetchProjectCategoriesSummary();

        // Merge API data with dummy data (descriptions, icons, etc)
        const mergedCategories = mergeCategoriesData(result.categories, allCategories);

        setCategories(mergedCategories);
        setSummary(result.summary);
        setFilteredCategories(mergedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter & search
  useEffect(() => {
    let result = [...categories];
    if (activeFilter !== 'Semua') {
      result = result.filter((c) => c.tag === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.tag.toLowerCase().includes(q)
      );
    }
    setFilteredCategories(result);
  }, [searchQuery, activeFilter, categories]);

  const handleCardClick = (category) => {
    const path = `/project-report/${category.id}`;

    navigate({
      pathname: path,
      search: searchParams.toString(),
    }, {
      state: { category }
    });
  };

  const allTags = ['Semua', ...new Set(categories.map((c) => c.tag))];

  if (isCheckingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw', background: '#f8fafc' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem', color: '#f97316' }}></i>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen px-9 py-8"
      style={{ background: 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 100%)' }}
    >
      {/* ── Page header ── */}
      <div className="mb-7">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 mb-2.5 text-xs text-slate-400">
          <span>Pages</span>
          <ChevronRight size={12} />
          <span className="text-orange-500 font-semibold">Project Categories</span>
        </div>

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[28px] font-extrabold text-slate-950 m-0 leading-[1.2] tracking-[-0.5px]">
              Project Categories
            </h1>
            <p className="mt-1.5 mb-0 text-sm text-slate-500">
              Pilih kategori untuk melihat daftar project yang tersedia
            </p>
          </div>

          {/* Live indicator */}
          <div
            className="flex items-center gap-2 bg-white border border-slate-200 rounded-[10px] py-2 px-3.5 text-xs text-slate-500"
          >
            <span
              className="inline-block w-2 h-2 rounded-full bg-green-500"
              style={{
                boxShadow: '0 0 6px #22c55e80',
                animation: 'pulse-green 2s infinite',
              }}
            />
            Data real-time
          </div>
        </div>
      </div>

      {/* ── Summary stats ── */}
      <div className="flex flex-wrap gap-3.5 mb-7">
        <SummaryCard icon={Layers} label="Total Kategori" value={summary?.totalCategory || 0} color="#6366f1" />
        <SummaryCard icon={FolderOpen} label="Total Project" value={summary?.totalProject || 0} color="#f97316" />
        <SummaryCard icon={TrendingUp} label="Project Aktif" value={summary?.activeProject || 0} color="#22c55e" />
        <SummaryCard icon={Clock} label="Selesai" value={summary?.completedProject || 0} color="#f59e0b" />
      </div>

      {/* ── Search + Filter bar ── */}
      <div className="flex items-center flex-wrap gap-3 mb-7">
        {/* Search */}
        <div className="relative max-w-95" style={{ flex: '1 1 280px' }}>
          <Search
            size={16}
            className="absolute pointer-events-none left-3.5 top-1/2 -translate-y-1/2 transition-colors"
            style={{ color: searchFocused ? '#f97316' : '#94a3b8' }}
          />
          <input
            type="text"
            placeholder="Cari kategori proyek..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            className="w-full box-border rounded-[10px] bg-white text-[13px] text-slate-950 transition-all"
            style={{
              padding: '10px 14px 10px 40px',
              border: searchFocused ? '1.5px solid #f97316' : '1.5px solid #e2e8f0',
              outline: 'none',
              boxShadow: searchFocused ? '0 0 0 3px #f9731620' : 'none',
            }}
          />
        </div>

        {/* Tag filters */}
        <div className="flex flex-wrap gap-2">
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveFilter(tag)}
              className="cursor-pointer text-xs font-semibold rounded-lg py-2 px-4 transition-all"
              style={{
                background: activeFilter === tag
                  ? 'linear-gradient(135deg, #f97316, #ea580c)'
                  : '#fff',
                color: activeFilter === tag ? '#fff' : '#64748b',
                boxShadow: activeFilter === tag
                  ? '0 4px 12px rgba(249,115,22,0.35)'
                  : '0 1px 4px rgba(0,0,0,0.06)',
                border: activeFilter === tag ? 'none' : '1px solid #e2e8f0',
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* ── Result count ── */}
      <div className="mb-[18px] text-[13px] text-slate-400">
        Menampilkan{' '}
        <span className="font-bold text-slate-950">{filteredCategories.length}</span>{' '}
        kategori
        {searchQuery && (
          <>
            {' '}untuk{' '}
            <span className="font-semibold text-orange-500">"{searchQuery}"</span>
          </>
        )}
      </div>

      {/* ── Grid ── */}
      {isLoading ? (
        /* Skeleton loader */
        <div
          className="grid gap-4.5"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          }}
        >
          {[...Array(9)].map((_, i) => (
            <div
              key={i}
              className="rounded-2xl h-[210px]"
              style={{
                background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.4s infinite',
              }}
            />
          ))}
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="text-center py-20 px-5 text-slate-400">
          <Search size={48} className="block mx-auto mb-4 opacity-30" />
          <div className="text-lg font-semibold text-slate-500">Kategori tidak ditemukan</div>
          <div className="text-[13px] mt-1.5">Coba ubah kata kunci atau filter yang digunakan</div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-5">
          {filteredCategories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* ── Keyframe styles injected ── */}
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 4px #22c55e80; }
          50%       { box-shadow: 0 0 10px #22c55ecc; }
        }
      `}</style>
    </div>
  );
};

export default ProjectCategories;
